import React, { useState, useEffect } from 'react'
import { FiEye, FiEyeOff, FiCheck, FiX } from 'react-icons/fi'

interface ValidationRule {
    id: string
    label: string
    isValid: boolean
}

interface PasswordValidationProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    showValidation?: boolean
    className?: string
    name?: string
    id?: string
    required?: boolean
}

const PasswordValidation: React.FC<PasswordValidationProps> = ({
    value,
    onChange,
    placeholder = "Enter your password",
    showValidation = true,
    className = "",
    name = "password",
    id = "password",
    required = false
}) => {
    const [showPassword, setShowPassword] = useState(false)
    const [hasStartedTyping, setHasStartedTyping] = useState(false)
    const [validationRules, setValidationRules] = useState<ValidationRule[]>([
        { id: 'length', label: '10 characters minimum', isValid: false },
        { id: 'number', label: '1 number', isValid: false },
        { id: 'lowercase', label: '1 lowercase letter', isValid: false },
        { id: 'uppercase', label: '1 capital letter', isValid: false },
        { id: 'special', label: '1 special character', isValid: false },
        { id: 'recurring', label: 'avoid 3 or more recurring characters', isValid: true }
    ])

    useEffect(() => {
        validatePassword(value)
    }, [value])

    const validatePassword = (password: string) => {
        const rules = [
            {
                id: 'length',
                label: '10 characters minimum',
                isValid: password.length >= 10
            },
            {
                id: 'number',
                label: '1 number',
                isValid: /\d/.test(password)
            },
            {
                id: 'lowercase',
                label: '1 lowercase letter',
                isValid: /[a-z]/.test(password)
            },
            {
                id: 'uppercase',
                label: '1 capital letter',
                isValid: /[A-Z]/.test(password)
            },
            {
                id: 'special',
                label: '1 special character',
                isValid: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
            },
            {
                id: 'recurring',
                label: 'avoid 3 or more recurring characters',
                isValid: !/(.)\1{2,}/.test(password)
            }
        ]

        setValidationRules(rules)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!hasStartedTyping) {
            setHasStartedTyping(true)
        }
        onChange(e.target.value)
    }

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword)
    }

    const getValidationIcon = (isValid: boolean) => {
        return isValid ? (
            <FiCheck className="w-3 h-3" />
        ) : (
            <FiX className="w-3 h-3" />
        )
    }

    const getValidationBadgeClasses = (isValid: boolean) => {
        return `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 ${isValid
            ? 'bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
            : 'bg-rose-100 text-rose-700 border border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800'
            }`
    }

    // Check if password has validation errors
    const hasValidationErrors = hasStartedTyping && value.length > 0 && !validationRules.every(rule => rule.isValid)

    // Check if all validations are met (requirements should be hidden)
    const allValidationsMet = hasStartedTyping && value.length > 0 && validationRules.every(rule => rule.isValid)

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Password Input Field */}
            <div className="relative">
                <input
                    id={id}
                    name={name}
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    onChange={handleInputChange}
                    required={required}
                    className={`block w-full px-4 py-4 text-base border-2 ${hasValidationErrors ? 'border-rose-300 focus:border-rose-500 dark:border-rose-400 dark:focus:border-rose-500'
                        : 'border-gray-300 focus:border-gray-500 dark:border-gray-600 dark:focus:border-gray-400'
                        } rounded-2xl placeholder-gray-500 text-gray-900 bg-white focus:outline-none focus:ring-0 transition-colors duration-200 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400 pr-12`}
                    placeholder={placeholder}
                    style={{
                        fontFamily: showPassword ? 'inherit' : 'text-security-disc, -webkit-text-security-disc, monospace'
                    } as React.CSSProperties & { WebkitTextSecurity?: string; MozTextSecurity?: string }}
                />

                {/* Eye Icon Toggle */}
                <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-4 flex items-center z-10 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-r-2xl transition-colors group"
                    onClick={togglePasswordVisibility}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                >
                    {showPassword ? (
                        <FiEyeOff className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-coral-600 dark:group-hover:text-coral-400 transition-colors" />
                    ) : (
                        <FiEye className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-coral-600 dark:group-hover:text-coral-400 transition-colors" />
                    )}
                </button>
            </div>

            {/* Validation Rules - only show after user starts typing and hide when all requirements are met */}
            {showValidation && hasStartedTyping && value.length > 0 && !allValidationsMet && (
                <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Password requirements:
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {validationRules.map((rule) => (
                            <div
                                key={rule.id}
                                className={getValidationBadgeClasses(rule.isValid)}
                            >
                                {getValidationIcon(rule.isValid)}
                                <span>{rule.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default PasswordValidation